import PolicyShell, { Section, SubHead, Bullets } from "@/components/PolicyShell";

export default function TermsPage() {
  return (
    <PolicyShell
      testId="terms-page"
      overline="BOSCAGE Elegance"
      title="Terms & Conditions"
      contact="Contact: hello@boscage.com  ·  www.boscage.com"
      lastUpdated="Last updated: June 2026  |  BOSCAGE Elegance"
    >
      <Section title="1. Product Description and Availability">
        <p>
          The website displays high-resolution images of the product in multiple angles and lighting conditions to
          provide an accurate representation of the product. Each color variant is showcased individually to avoid
          confusion.
        </p>
        <p>
          <strong className="text-[#000000]">Color Representation:</strong> Despite efforts to display accurate colors,
          variations might occur due to individual screen settings or differences in device displays. A disclaimer is
          provided on the website to inform customers about the possibility of slight color variations between the
          displayed image and the actual product.
        </p>
        <p>
          <strong className="text-[#000000]">Product Availability:</strong> The website explicitly mentions the
          availability of each color variant in real-time. If a particular color or size is out of stock, it is
          clearly indicated, allowing customers to make informed decisions.
        </p>
        <p>
          <strong className="text-[#000000]">Description Accuracy:</strong> The product&apos;s description includes
          comprehensive details regarding fabric, leather composition, sizing charts, care instructions, and any
          distinctive features. This ensures customers have a clear understanding of the product before making a
          purchase.
        </p>

        <SubHead>Resolution Options</SubHead>
        <p>
          <strong className="text-[#000000]">Refund or Exchange Policy:</strong> In case a customer receives an item
          in a different color than expected due to a discrepancy between the actual item and the displayed image,
          they are provided with hassle-free options. This includes the choice for a full refund or an exchange for
          the correct color variant, depending on their preference.
        </p>
        <p>
          <strong className="text-[#000000]">Return Procedure:</strong> A clear and straightforward return procedure
          is outlined, detailing the steps customers need to take to initiate a return or exchange request. This
          involves contacting customer support, providing necessary details, and returning the item in its original
          condition with tags intact.
        </p>
        <p>
          <strong className="text-[#000000]">Customer Assistance:</strong> A dedicated customer support team is
          available 24/7 to assist customers with any queries or concerns related to product colors, sizing, or
          returns.
        </p>

        <SubHead>Discrepancy Resolution Process</SubHead>
        <p>
          <strong className="text-[#000000]">Communication Channel:</strong> Customers are encouraged to reach out to
          our dedicated customer support team via email (hello@boscage.com) within 14 days from the date of receiving
          the product, highlighting the discrepancy encountered.
        </p>
        <p>
          <strong className="text-[#000000]">Verification Procedure:</strong> Upon contact, our team will guide
          customers through a straightforward verification process to validate the received item against the ordered
          variant. This may include requesting photographic evidence.
        </p>
        <p>
          <strong className="text-[#000000]">Flexible Options:</strong> Customers will be provided with flexible
          options to rectify the situation, such as:
        </p>
        <Bullets
          items={[
            "A hassle-free exchange for the correct color variant, subject to availability.",
            "A full refund, including any incurred shipping costs, if an exchange is not feasible or preferred.",
          ]}
        />
        <p>
          <strong className="text-[#000000]">Prompt Resolution:</strong> BOSCAGE commits to processing refunds or
          exchanges within 14 days from the date of initiating the return request.
        </p>
      </Section>

      <Section title="2. Pricing Information">
        <p>
          <strong className="text-[#000000]">Transparent Pricing:</strong> BOSCAGE ensures transparent pricing
          displayed on the website for all listed products. The prices showcased are inclusive of applicable taxes and
          clearly indicate any shipping fees or additional charges.
        </p>
        <p>
          <strong className="text-[#000000]">Currency Specification:</strong> Prices are presented in USD for US
          customers, with the option to view prices in alternative currencies for international customers, subject to
          currency conversion rates.
        </p>
        <p>
          <strong className="text-[#000000]">Accepted Payment Methods:</strong> BOSCAGE offers a variety of secure
          payment methods including credit/debit cards, digital wallets, bank transfers, and other verified payment
          gateways.
        </p>
        <p>
          <strong className="text-[#000000]">Checkout Transparency:</strong> During the checkout process, customers
          are provided with a detailed breakdown of the final cost, encompassing the product price, taxes, shipping
          charges, and any applicable discounts or promotional offers.
        </p>
        <p>
          <strong className="text-[#000000]">Payment Failure Disclaimer:</strong> While utmost care is taken to
          facilitate smooth payment processing, BOSCAGE disclaims liability for payment failures resulting from
          external factors beyond its control, including technical issues with payment gateways, banking institutions,
          or connectivity problems.
        </p>
        <p>
          <strong className="text-[#000000]">Security Measures:</strong> BOSCAGE implements robust encryption and
          security protocols to safeguard payment information during transactions.
        </p>
      </Section>

      <Section title="3. Estimated Delivery Timelines">
        <p>
          BOSCAGE provides estimated delivery timelines at the time of order placement, based on the shipping method
          and destination. This estimate encompasses the time required for order processing, packaging, and transit.
        </p>
        <p>
          <strong className="text-[#000000]">Shipping Notification and Tracking:</strong> Upon order fulfillment and
          dispatch, customers receive a shipping confirmation email containing tracking information to monitor the
          progress of their shipment.
        </p>
        <p>
          <strong className="text-[#000000]">Communication of Shipping Delays:</strong> In the event of any
          unforeseen delays, BOSCAGE promptly notifies customers via email with a revised estimated delivery date.
        </p>
        <p>
          <strong className="text-[#000000]">Lost or Undelivered Items:</strong> In the rare instance of a lost or
          undelivered item, our customer support team investigates the issue in collaboration with the shipping
          carrier. Where non-delivery is confirmed, BOSCAGE offers a replacement or refund.
        </p>
        <p>
          <strong className="text-[#000000]">Delivery to Specified Address:</strong> Orders are delivered to the
          address provided by the customer during checkout. It is the customer&apos;s responsibility to ensure the
          accuracy and completeness of the delivery address.
        </p>
        <p>
          <strong className="text-[#000000]">Signature Confirmation:</strong> For certain high-value deliveries (such
          as luxury leather jackets), a signature may be required upon receipt to confirm successful delivery and
          protect against delivery disputes.
        </p>
      </Section>

      <Section title="4. Return Policy and Duration">
        <p>
          BOSCAGE provides a return window allowing customers to return items within 14 days from the date of receipt
          for reasons such as sizing discrepancies or faulty/mis-described products.
        </p>

        <SubHead>Conditions for Accepting Returns</SubHead>
        <p>Returns are accepted for items that meet all of the following criteria:</p>
        <Bullets
          items={[
            "The item must be in new, unused condition, with all original tags and packaging intact.",
            "Products must not exhibit any signs of wear, alteration, or damage.",
            "Items must be accompanied by a copy of the packing slip or invoice.",
            'Personalized or bespoke items (e.g., those created through our "Custom Fit" service) are ineligible for return unless deemed faulty or mis-described.',
          ]}
        />

        <SubHead>Refund and Return Process</SubHead>
        <p>
          <strong className="text-[#000000]">Refund Request Period:</strong> Customers may request refunds within 14
          days of receiving a product that is faulty, mis-described, or if the sizing does not match expectations.
        </p>
        <p>
          <strong className="text-[#000000]">Return Shipping:</strong> BOSCAGE arranges return shipping and covers
          associated costs in cases of faulty or mis-described items. For all other returns, return shipping costs are
          the customer&apos;s responsibility.
        </p>
        <p>
          <strong className="text-[#000000]">Refund Processing Time:</strong> Upon receiving the returned product,
          refunds are processed within 5&ndash;7 business days to the original payment method.
        </p>

        <SubHead>Cancellation Policy</SubHead>
        <p>
          Your right to cancel a Contract starts from the date of the Order Confirmation email. You may cancel your
          order free of charge within 12 hours of placing it.
        </p>
        <p>
          <strong className="text-[#000000]">Cancellation after 12 hours:</strong> Orders canceled after 12 hours are
          subject to a 10% cancellation fee to cover processing costs. Once an order has shipped, it cannot be
          canceled &mdash; however, you may return it once received by following our standard returns process.
        </p>
        <p>To cancel, email us at hello@boscage.com with your order number as soon as possible.</p>

        <SubHead>Refund Details Upon Cancellation</SubHead>
        <p>If you cancel your Contract, we will:</p>
        <Bullets
          items={[
            "Refund the price paid for the Products (we may reduce the refund to reflect any reduction in value caused by unacceptable handling).",
            "Refund delivery costs up to the amount of the least expensive standard delivery method we offer.",
            "Process refunds within 14 days of receiving the returned item, or within 14 days of your cancellation notice if the item has not yet been dispatched.",
            "Arrange collection and cover return costs if the products are faulty or mis-described.",
          ]}
        />
        <p>
          We stand behind the quality of our products. If you receive a faulty item, please contact us immediately for
          a resolution in accordance with US consumer protection guidelines.
        </p>
      </Section>

      <Section title="5. Delivery">
        <p>
          5.1 We will contact you with an estimated delivery date, which will be within 30 days after the date of the
          Order Confirmation.
        </p>
        <p>
          5.2 If no one is available at your address to take delivery, we will leave you a note that the Products have
          been returned to our premises &mdash; please contact the carrier to rearrange delivery.
        </p>
        <p>
          5.3 Delivery of an order shall be completed when we deliver the Products to the address you gave us, and the
          Products will be your responsibility from that time.
        </p>
        <p>
          5.4 You own the Products once we have received payment in full, including all applicable delivery charges.
        </p>
        <p>
          5.5 If we miss the 30-day delivery deadline, you may cancel your order straight away if: (a) we have refused
          to deliver the Products; (b) delivery within the deadline was essential; or (c) you told us before we
          accepted your order that delivery within the deadline was essential.
        </p>
        <p>
          5.6 If you do not wish to cancel straight away, you may give us a new reasonable deadline for delivery, and
          you can cancel your order if we do not meet the new deadline.
        </p>
      </Section>

      <Section title="6. International Delivery">
        <p>
          6.1 We deliver worldwide. However, there are restrictions on some Products for certain international
          destinations (such as import restrictions or bans) which are outside our control. Please check whether any
          such restrictions apply before ordering.
        </p>
        <p>
          6.2 Orders for international delivery may be subject to import duties and taxes applied upon arrival in the
          destination country. We have no control over these charges and cannot predict their amount.
        </p>
        <p>
          6.3 You will be responsible for payment of any such import duties and taxes. Please contact your local
          customs office for further information before placing your order.
        </p>
        <p>
          6.4 You must comply with all applicable laws and regulations of the country for which the Products are
          destined. We will not be liable if you breach any such law.
        </p>
      </Section>

      <Section title="7. Limited Stock Items">
        <p>
          <strong className="text-[#000000]">Stock Quantity Disclosure:</strong> BOSCAGE provides visibility on the
          limited stock quantity available for specific items, displaying remaining units or indicating low stock
          alerts on the product page.
        </p>
        <p>
          <strong className="text-[#000000]">Real-time Stock Status:</strong> The website ensures real-time updates on
          stock availability, promptly reflecting changes in stock quantities.
        </p>
        <p>
          <strong className="text-[#000000]">Backorder Opportunities:</strong> When an item is out of stock, customers
          may have the option to place a backorder, securing the item for future delivery once restocked.
        </p>
        <p>
          <strong className="text-[#000000]">Pre-order Reservation:</strong> Customers can reserve limited stock items
          by pre-ordering, securing their purchase before the item becomes available for general sale.
        </p>
        <p>
          <strong className="text-[#000000]">Stock Alert Notifications:</strong> Customers can opt in to receive email
          notifications when a previously out-of-stock item becomes available again.
        </p>
      </Section>

      <Section title="8. Price of Products and Delivery Charges">
        <p>
          8.1 The prices of the Products will be as quoted on our site at the time you submit your order. We take all
          reasonable care to ensure prices are correct.
        </p>
        <p>
          8.2 Prices for our Products may change from time to time, but changes will not affect any order you have
          already placed.
        </p>
        <p>
          8.3 The price of a Product includes applicable sales tax at the rate in effect at the time of your order
          for the delivery state.
        </p>
        <p>
          8.4 The price of a Product does not include delivery charges. Delivery charges are advised to you during the
          checkout process.
        </p>
        <p>8.5 If a pricing error is discovered:</p>
        <Bullets
          items={[
            "If the correct price is lower than stated, we will charge the lower amount when dispatching.",
            "If the correct price is higher than stated, we will contact you in writing and give you the option to purchase at the correct price or cancel your order.",
          ]}
        />
      </Section>

      <Section title="9. Our Right to Update These Terms and Conditions">
        <p>
          We reserve the right to update these Terms and Conditions at any time. Please visit this page regularly to
          stay informed of any changes. The date of the most recent update will be clearly displayed.
        </p>
        <p>
          For each product order you place, the Terms and Conditions in effect at the time of your order will govern
          the contract between you and us.
        </p>
        <p>
          We may revise these Terms and Conditions in circumstances such as changes in relevant laws and regulations,
          or changes in accepted payment methods. Where revisions affect a pending order, we will notify you by email
          with reasonable advance notice, detailing the changes, reasons, effective date, and your right to cancel.
        </p>
      </Section>

      <Section title="Contact Us">
        <p>If you have any questions about these Terms and Conditions, please contact our customer service team:</p>
        <p>
          Email: hello@boscage.com
          <br />
          Website: www.boscage.com
        </p>
      </Section>
    </PolicyShell>
  );
}
